import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const UploadingClips: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          FOR CREATORS
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Uploading Short Clips
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          IndeeClips Shorts (Clips) are vertical, short-form videos designed for quick, engaging content. Similar to Instagram Reels or YouTube Shorts, clips are perfect for capturing attention with bite-sized entertainment, tutorials, or creative moments that viewers can watch on the go.
        </Typography>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          What Are Clips?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Clips are short, vertical videos optimized for mobile viewing. They're ideal for content that's 15 to 60 seconds long and designed to be consumed quickly in a continuous feed. Clips help creators reach new audiences who prefer snackable content.
        </Typography>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          How to Upload a Clip
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Sign in to your IndeeClips account and ensure you have a channel created
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Navigate to the Clips section or click "Upload Clip" from your channel page
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Select your vertical video file (9:16 aspect ratio recommended)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Add a catchy title that describes your clip content (keep it concise)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Write a brief description and add relevant hashtags to increase discoverability
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Upload a custom thumbnail or select an auto-generated frame from your video
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Click "Upload" and your clip will be processed and added to the Clips feed
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Clips Specifications
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Duration:</strong> 15 seconds to 60 seconds (optimal for viewer retention)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Aspect ratio:</strong> 9:16 (vertical) recommended, 1080x1920 pixels ideal resolution
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>File format:</strong> MP4, MOV (MP4 strongly recommended)
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Maximum file size:</strong> 500 MB per clip
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Frame rate:</strong> 30 FPS recommended for smooth playback
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Tips for Creating Successful Clips
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Hook viewers immediately:</strong> The first 2-3 seconds are crucial. Start with something attention-grabbing
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Use vertical format:</strong> Always shoot in vertical orientation for optimal mobile viewing experience
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Add text overlays:</strong> Many viewers watch without sound, so on-screen text helps convey your message
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Include trending hashtags:</strong> Use popular and relevant hashtags to increase your clip's reach
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Post consistently:</strong> Regular posting helps build your audience and keeps viewers engaged
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Optimize for Indian audiences:</strong> Consider regional languages, trending topics, and cultural moments
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Clips vs Regular Videos
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          While regular videos are great for in-depth content, clips excel at quick engagement. Clips appear in a dedicated feed where viewers can scroll through multiple videos rapidly. They're perfect for teasers, highlights, challenges, or standalone short-form content. You can upload both clips and regular videos to maximize your channel's reach.
        </Typography>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Need Help with Clips?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            If you're experiencing issues uploading clips or have questions about optimizing your short-form content strategy, we're here to assist. Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default UploadingClips;
